// ===========================================
// Registration fee credit hooks (#374 Phase 3)
// ===========================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { registrationFeeSettingsApi, type RegistrationFeeSettingsPatch } from '../api/registrationFeeSettings.api';
import { invoiceKeys } from './useInvoices';
import { apiErrorMessage } from '../utils/apiError';

export const registrationFeeKeys = {
  all: ['registration-fee'] as const,
  settings: () => [...registrationFeeKeys.all, 'settings'] as const,
  preview: () => [...registrationFeeKeys.all, 'preview'] as const,
  // Under invoiceKeys.all: every invoice write refreshes the Held list (a credit spends a fee).
  held: (familyId: string, invoiceId: string) => [...invoiceKeys.all, 'registration-credits-held', familyId, invoiceId] as const,
};

export function useRegistrationFeeSettings(enabled = true) {
  return useQuery({ queryKey: registrationFeeKeys.settings(), queryFn: registrationFeeSettingsApi.get, enabled });
}

/** The owner's switch preview (owner-only route): fetched only while `enabled`. */
export function useRegistrationFeePreview(enabled: boolean) {
  return useQuery({ queryKey: registrationFeeKeys.preview(), queryFn: registrationFeeSettingsApi.preview, enabled });
}

export function useUpdateRegistrationFeeSettings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (patch: RegistrationFeeSettingsPatch) => registrationFeeSettingsApi.update(patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: registrationFeeKeys.all });
      // Which fees read "Held" on Payment History and the report changes with the switch.
      queryClient.invalidateQueries({ queryKey: invoiceKeys.all });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      enqueueSnackbar('Registration fee settings saved', { variant: 'success' });
    },
    onError: (error: unknown) => {
      enqueueSnackbar(apiErrorMessage(error, 'Failed to save the registration fee settings'), { variant: 'error' });
    },
  });
}

/**
 * The children of THIS draft whose fee is Held and who are provably on it (linked enrolment): the
 * server's own rule, so the dialog never offers a child it would refuse (#374 /qa H1).
 */
export function useHeldRegistrationCredits(familyId: string | undefined, invoiceId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: registrationFeeKeys.held(familyId ?? '', invoiceId ?? ''),
    queryFn: () => registrationFeeSettingsApi.held(familyId as string, invoiceId as string),
    enabled: enabled && !!familyId && !!invoiceId,
  });
}
